/**
 * VersichererFristenEinstellungen.tsx - P4-S7 admin form.
 *
 * The per-tenant Versicherer-Benachrichtigung Fristen, mounted on Administration › Versicherer-Fristen.
 * Stefan's defaults are seeds (a «Standard» hint shows while a prozess has never been edited); saving
 * a row PUTs it. Inline error on a 400. Read and save go through TanStack Query; both routes need
 * mahnwesen_config.manage (the api is the hard line).
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { INSURER_NOTIFICATION_PROZESS_LABEL } from '@brokly/shared';
import type { InsurerNotificationFristConfigDto } from '@brokly/shared';
import { useSetVersichererFristen, useVersichererFristen } from '../mandate/versicherer-benachrichtigung-queries';

type FristField = 'frist_1_arbeitstage' | 'frist_2_arbeitstage' | 'erinnerungen';

export function VersichererFristenEinstellungen() {
  const fristen = useVersichererFristen();
  const save = useSetVersichererFristen();
  // The edits not yet saved, per prozess, over the loaded rows.
  const [edits, setEdits] = useState<Record<string, Partial<Record<FristField, number>>>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [savingProzess, setSavingProzess] = useState<string | null>(null);

  function patch(prozess: string, field: FristField, value: number) {
    setEdits((prev) => ({ ...prev, [prozess]: { ...prev[prozess], [field]: value } }));
  }

  function onSave(row: InsurerNotificationFristConfigDto) {
    setSavingProzess(row.prozess);
    setRowError((e) => ({ ...e, [row.prozess]: '' }));
    save.mutate(
      {
        prozess: row.prozess as 'p4_analyse' | 'p5_broker' | 'p6_aufloesung',
        frist_1_arbeitstage: row.frist_1_arbeitstage,
        frist_2_arbeitstage: row.frist_2_arbeitstage,
        erinnerungen: row.erinnerungen,
      },
      {
        onSuccess: () => setEdits((prev) => ({ ...prev, [row.prozess]: {} })),
        onError: () => setRowError((e) => ({ ...e, [row.prozess]: 'Werte ausserhalb des zulässigen Bereichs.' })),
        onSettled: () => setSavingProzess(null),
      },
    );
  }

  if (fristen.isError) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" />
        Die Fristen konnten nicht geladen werden.
      </p>
    );
  }
  if (fristen.data === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt …
      </p>
    );
  }
  const rows = fristen.data.map((r) => ({ ...r, ...edits[r.prozess] }));

  return (
    <section className="flex flex-col gap-3" aria-label="Versicherer-Fristen">
      {rows.map((row) => (
        <div key={row.prozess} className="flex flex-col gap-2 rounded-lg border bg-card p-4" data-testid="vfr-row">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{INSURER_NOTIFICATION_PROZESS_LABEL[row.prozess] ?? row.prozess}</h3>
            {row.ist_default && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground" data-testid="vfr-standard">
                Standard
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs">
              Frist 1 (Arbeitstage)
              <input
                type="number"
                className="w-24 rounded border px-2 py-1 text-sm"
                value={row.frist_1_arbeitstage}
                aria-label={`Frist 1 ${row.prozess}`}
                onChange={(e) => patch(row.prozess, 'frist_1_arbeitstage', Number(e.target.value))}
              />
            </label>
            <label className="flex flex-col text-xs">
              Frist 2 (Arbeitstage)
              <input
                type="number"
                className="w-24 rounded border px-2 py-1 text-sm"
                value={row.frist_2_arbeitstage}
                aria-label={`Frist 2 ${row.prozess}`}
                onChange={(e) => patch(row.prozess, 'frist_2_arbeitstage', Number(e.target.value))}
              />
            </label>
            <label className="flex flex-col text-xs">
              Erinnerungen
              <input
                type="number"
                className="w-24 rounded border px-2 py-1 text-sm"
                value={row.erinnerungen}
                aria-label={`Erinnerungen ${row.prozess}`}
                onChange={(e) => patch(row.prozess, 'erinnerungen', Number(e.target.value))}
              />
            </label>
            <Button
              size="sm"
              disabled={savingProzess === row.prozess}
              onClick={() => onSave(row)}
              data-testid={`vfr-save-${row.prozess}`}
            >
              Speichern
            </Button>
          </div>
          {rowError[row.prozess] != null && rowError[row.prozess] !== '' && (
            <p className="text-sm text-destructive" role="alert">
              {rowError[row.prozess]}
            </p>
          )}
        </div>
      ))}
    </section>
  );
}
